import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '../../lib/api';
import { LocationBreadcrumb } from './location-breadcrumb';
import type { LocationNode, LocationTypeItem } from './types';
import { SkeletonBlock, SkeletonRows } from '../skeleton';

/**
 * S5 位置检查器（右侧抽屉）。M2 交付"概览"内容——类型/可存储/状态、
 * 名称与描述编辑（乐观 version）、归档（含子树预览与级联确认）。
 * 库存/预警/历史标签页依赖 M5/M8 尚未建立的数据，留给对应里程碑接上。
 */
export function LocationInspector({
  locationId,
  canManage,
  onClose,
  onNavigate,
  onCreateChild,
  onChanged,
  onArchived,
}: {
  readonly locationId: string;
  readonly canManage: boolean;
  readonly onClose: () => void;
  readonly onNavigate: (id: string) => void;
  readonly onCreateChild: (parentId: string) => void;
  readonly onChanged: () => void;
  readonly onArchived: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: node } = useQuery({
    queryKey: ['locations', 'detail', locationId],
    queryFn: () => apiFetch<LocationNode>(`/locations/${locationId}`),
  });
  const { data: typesData } = useQuery({
    queryKey: ['location-types'],
    queryFn: () => apiFetch<{ items: LocationTypeItem[] }>('/location-types'),
  });

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [canStoreInventory, setCanStoreInventory] = useState(true);
  const [typeId, setTypeId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showArchive, setShowArchive] = useState(false);

  useEffect(() => {
    if (!node) return;
    setName(node.name);
    setDescription(node.description ?? '');
    setCanStoreInventory(node.canStoreInventory);
    setTypeId(node.typeId ?? '');
  }, [node]);

  const save = async (): Promise<void> => {
    if (!node) return;
    setSaving(true);
    setError(null);
    try {
      await apiFetch(`/locations/${locationId}`, {
        method: 'PATCH',
        body: {
          name,
          description: description || undefined,
          canStoreInventory,
          typeId: typeId || undefined,
          version: node.version,
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['locations', 'detail', locationId] });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <aside className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer__header">
          <div>
            <LocationBreadcrumb locationId={locationId} onNavigate={onNavigate} />
            <h2 className="drawer__title">{node?.name}</h2>
          </div>
          <button className="btn btn--ghost btn--sm" onClick={onClose}>
            {t('app.close')}
          </button>
        </div>

        {!node ? (
          <SkeletonBlock height={120} />
        ) : (
          <>
            {!node.canStoreInventory && (
              <p className="field-hint" style={{ color: 'var(--color-warning)' }}>
                {t('locations.notStorageHint')}
              </p>
            )}

            <label className="field">
              <span>{t('locations.name')}</span>
              <input value={name} onChange={(e) => setName(e.target.value)} disabled={!canManage} />
            </label>

            <label className="field">
              <span>{t('locations.type')}</span>
              <select
                value={typeId}
                onChange={(e) => setTypeId(e.target.value)}
                disabled={!canManage}
              >
                <option value="">—</option>
                {(typesData?.items ?? []).map((type) => (
                  <option key={type.id} value={type.id}>
                    {t(type.nameKey, type.code)}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span>{t('locations.description')}</span>
              <input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={!canManage}
              />
            </label>

            <label className="row" style={{ marginBottom: 'var(--space-4)' }}>
              <input
                type="checkbox"
                checked={canStoreInventory}
                onChange={(e) => setCanStoreInventory(e.target.checked)}
                disabled={!canManage}
              />
              {t('locations.canStoreInventory')}
            </label>

            {error && <p className="field-error">{error}</p>}

            {canManage && (
              <div className="row" style={{ marginBottom: 'var(--space-6)' }}>
                <button
                  className="btn btn--primary btn--sm"
                  disabled={saving}
                  onClick={() => void save()}
                >
                  {t('app.save')}
                </button>
                <button
                  className="btn btn--ghost btn--sm"
                  onClick={() => onCreateChild(locationId)}
                >
                  {t('locations.createChild')}
                </button>
                <button className="btn btn--danger btn--sm" onClick={() => setShowArchive(true)}>
                  {t('locations.archive')}
                </button>
              </div>
            )}
          </>
        )}
      </aside>

      {showArchive && (
        <ArchiveConfirmDialog
          locationId={locationId}
          locationName={node?.name ?? ''}
          onClose={() => setShowArchive(false)}
          onArchived={() => {
            setShowArchive(false);
            onArchived();
          }}
        />
      )}
    </div>
  );
}

interface ArchivePreview {
  readonly descendants: readonly LocationNode[];
  readonly hasStock: boolean;
}

/** M3a：布局编辑器的"删除→归档"复用同一套影响预览与级联确认，不另起一套 */
export function ArchiveConfirmDialog({
  locationId,
  locationName,
  onClose,
  onArchived,
}: {
  readonly locationId: string;
  readonly locationName: string;
  readonly onClose: () => void;
  readonly onArchived: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { data: preview, isPending } = useQuery({
    queryKey: ['locations', locationId, 'archive-preview'],
    queryFn: () => apiFetch<ArchivePreview>(`/locations/${locationId}/archive-preview`),
  });

  const confirm = async (): Promise<void> => {
    setSubmitting(true);
    setError(null);
    try {
      await apiFetch(`/locations/${locationId}/archive`, {
        method: 'POST',
        body: { cascade: (preview?.descendants.length ?? 0) > 0 },
      });
      onArchived();
    } catch (err) {
      setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const blocked = preview?.hasStock ?? false;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{t('locations.archiveConfirmTitle', { name: locationName })}</h2>

        {isPending && <SkeletonRows rows={2} rowHeight={32} bars={3} />}

        {preview && (
          <>
            {blocked && (
              <p className="field-error">{t('errors:LOCATION.CANNOT_ARCHIVE_WITH_STOCK')}</p>
            )}
            {!blocked && preview.descendants.length > 0 && (
              <>
                <p>{t('locations.archiveCascadeWarning', { count: preview.descendants.length })}</p>
                <ul>
                  {preview.descendants.slice(0, 10).map((d) => (
                    <li key={d.id}>{d.name}</li>
                  ))}
                  {preview.descendants.length > 10 && <li>…</li>}
                </ul>
              </>
            )}
            {!blocked && preview.descendants.length === 0 && (
              <p>{t('locations.archiveLeafConfirm')}</p>
            )}
          </>
        )}

        {error && <p className="field-error">{error}</p>}

        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t('app.cancel')}
          </button>
          <button
            className="btn btn--danger"
            disabled={submitting || blocked || isPending}
            onClick={() => void confirm()}
          >
            {t('locations.archive')}
          </button>
        </div>
      </div>
    </div>
  );
}
